import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { FaPlus, FaTrash, FaPencilAlt } from "react-icons/fa";
import { CreatableSelect } from "chakra-react-select";
import useChecklistItems from "../../hooks/useChecklistItems";
import axios from "../../axiosConfig";

const getWidgetLabel = (widget) => widget?.title || widget?.name || "";

const sortWidgetsByLabel = (widgets) =>
  [...widgets].sort((a, b) =>
    getWidgetLabel(a).localeCompare(getWidgetLabel(b), undefined, {
      sensitivity: "base",
    })
  );

const InterpretationChecklistManager = ({ interpretationId }) => {
  const { items, loading, createItem, updateItem, deleteItem } =
    useChecklistItems(interpretationId);

  const [label, setLabel] = useState("");
  const [linkedWidgetId, setLinkedWidgetId] = useState("");
  const [category, setCategory] = useState("");
  const [error, setError] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetch all widgets to allow linking
  React.useEffect(() => {
    const fetchWidgets = async () => {
      try {
        const response = await axios.get("/widgets");
        setWidgets(sortWidgetsByLabel(response.data || []));
      } catch (error) {
        console.error("Error fetching widgets:", error);
      }
    };
    fetchWidgets();
  }, []);

  // Derive unique categories from current items
  const existingCategories = useMemo(() => {
    const set = new Set(
      (items || [])
        .map((i) => (i.category || "").trim())
        .filter((c) => c.length > 0)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const categoryOptions = useMemo(
    () => existingCategories.map((c) => ({ label: c, value: c })),
    [existingCategories]
  );

  const resetForm = () => {
    setLabel("");
    setLinkedWidgetId("");
    setCategory("");
    setError("");
    setEditingItem(null);
  };

  const startCreate = () => {
    resetForm();
    onOpen();
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setLabel(item.label || "");
    setLinkedWidgetId(item.linked_widget_id || "");
    setCategory(item.category || "");
    onOpen();
  };

  const handleSubmit = async () => {
    if (!label.trim()) {
      setError("Label is required");
      return;
    }

    const payload = {
      label: label.trim(),
      linked_widget_id: linkedWidgetId || null,
      category: category.trim(),
    };

    if (editingItem) {
      await updateItem(editingItem.id, payload);
    } else {
      await createItem(payload);
    }

    resetForm();
    onClose();
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" p={4}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box mt={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <Button leftIcon={<FaPlus />} colorScheme="green" onClick={startCreate}>
          Add Checklist Item
        </Button>
      </Flex>

      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Label</Th>
              <Th>Category</Th>
              <Th>Linked Widget</Th>
              <Th width="120px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {items.map((item) => (
              <Tr key={item.id}>
                <Td>{item.label}</Td>
                <Td>{item.category || "—"}</Td>
                <Td>
                  {item.linked_widget_id
                    ? widgets.find((w) => w.id === item.linked_widget_id)?.title || "—"
                    : "—"}
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FaPencilAlt />}
                      aria-label="Edit"
                      size="sm"
                      onClick={() => startEdit(item)}
                    />
                    <IconButton
                      icon={<FaTrash />}
                      aria-label="Delete"
                      size="sm"
                      colorScheme="red"
                      onClick={() => deleteItem(item.id)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Modal for Add/Edit */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingItem ? "Edit Checklist Item" : "Add Checklist Item"}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!error}>
                <FormLabel>Label</FormLabel>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Enter checklist item label"
                />
                {error && <FormErrorMessage>{error}</FormErrorMessage>}
              </FormControl>

              <FormControl>
                <FormLabel>Category</FormLabel>
                <CreatableSelect
                  isClearable
                  placeholder="Select or create a category..."
                  options={categoryOptions}
                  value={category ? { label: category, value: category } : null}
                  onChange={(opt) => setCategory(opt ? opt.value : "")}
                  onCreateOption={(val) => setCategory((val || "").trim())}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Linked Widget (optional)</FormLabel>
                <Select
                  placeholder="Select a widget"
                  value={linkedWidgetId}
                  onChange={(e) => setLinkedWidgetId(e.target.value)}
                >
                  {widgets.map((w) => (
                    <option key={w.id} value={w.id}>
                      {getWidgetLabel(w)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button onClick={handleSubmit} colorScheme="blue">
                {editingItem ? "Save Changes" : "Add Item"}
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                variant="ghost"
              >
                Cancel
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default InterpretationChecklistManager;
